import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DEFAULT_PAGE_LIMIT,
  type ContractTypeProposalsResponse,
  type Notification,
  type NotificationListResponse,
  type VerificationQueueResponse,
} from '@clm/shared-types';

import { fetchContractTypeProposals } from '../api/contract-type-proposals';
import { ApiError } from '../api/errors';
import { fetchNotifications } from '../api/notifications';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { fetchVerificationQueue } from '../api/verifications';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { TagInput } from '../components/TagInput';
import { requireSurface } from '../routes/surfaces';
import { TYPE_PROPOSALS_TAB_LABEL, typeProposalTabCounts } from './type-proposals-tabs';

/**
 * Needs attention — O-1, FR-04, FR-07, NFR-15. The landing surface.
 *
 * ## Two panels, each labelled as what it is
 *
 * *"Ordered by what lapses first"* is tier arithmetic, and the tier arithmetic
 * belongs with the notification rules — server-side, next to the
 * `(rule, contract, occurrence_date, tier)` idempotency key that makes the
 * ladder a control rather than a mailing list (§3.2, §3.3). So this surface
 * reads the ladder from `GET /v1/notifications` and does **not** compose a
 * ranking of its own.
 *
 * The earlier idea — fuse the notification totals with a contract search for
 * `expiring_to = today + 90d` — is deliberately not built. Two calls resolve
 * scope independently, so the fused list would carry two different scope
 * snapshots and a total that matches neither, and the ranking would be a browser
 * inventing precedence between a renewal date and an unverified field. The two
 * panels are therefore separate and separately labelled: **what has been alerted
 * on and not acknowledged**, and **what is awaiting verification**. Nothing here
 * presents one fused number.
 *
 * ## A third panel, for administrators only (CLM-118, R-118-16, B30)
 *
 * **Contract types (administrators)** counts the type decisions waiting for an
 * Admin — AI disagrees, Type pending, AI decided — verify, and Possibly an
 * amendment — from the Type proposals endpoint's own totals, the same counts the
 * Type proposals tabs show, with a link there. It is rendered only when
 * `mayAttempt(identity, ['admin'])` (usability only; the API's
 * `admin:configure` is the gate), and a 403 renders nothing. That endpoint takes
 * no parameters, so the panel is never narrowed by the tag filter — it says so,
 * and the filter's label ("filters both panels") stays true of the two it
 * filters.
 *
 * ## The ladder may not exist yet, and this page says so
 *
 * On the branch this was built from, `apps/api/src/routes/handlers.ts` still has
 * `export const notifications = notImplemented`, so the endpoint answers `501`
 * — *the authorisation path ran and passed; the behaviour behind it does not
 * exist yet*. That is rendered as the not-built state naming the endpoint, not
 * as an error and not as an empty ladder. An empty ladder would be a claim that
 * nothing needs attention, which is the exact failure O-1 describes: the control
 * failing silently while the dashboard stays green.
 */

type Panel<TResponse> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly response: TResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

function toProblem(error: unknown): Extract<Panel<never>, { kind: 'problem' }> {
  return {
    kind: 'problem',
    problem: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

/** The roles the gateway grants `admin:configure` to — a display hint, never the gate (D-110-2). */
const TYPE_DECIDER_ROLES: readonly string[] = ['admin'];

export function NeedsAttention() {
  const surface = requireSurface('dash');
  const { identity } = useIdentity();
  const showTypeDecisions = mayAttempt(identity, TYPE_DECIDER_ROLES);

  const [ladder, setLadder] = useState<Panel<NotificationListResponse>>({ kind: 'loading' });
  const [queue, setQueue] = useState<Panel<VerificationQueueResponse>>({ kind: 'loading' });
  /** CLM-77 — one all-of tag filter, applied to both panels' requests. */
  const [tags, setTags] = useState<readonly string[]>([]);
  const tagFilter = tags.length > 0 ? { tags: [...tags] } : {};

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    void (async () => {
      try {
        // Unacknowledged only — `include_acknowledged` omitted. The state that
        // needs action is the only state this surface is about.
        const response = await fetchNotifications(
          { page: { limit: DEFAULT_PAGE_LIMIT }, ...tagFilter },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setLadder({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setLadder(toProblem(error));
      }
    })();

    void (async () => {
      try {
        const response = await fetchVerificationQueue(
          { sort: 'oldest_first', page: { limit: DEFAULT_PAGE_LIMIT }, ...tagFilter },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setQueue({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setQueue(toProblem(error));
      }
    })();

    return () => {
      controller.abort();
    };
    // `tags` is the only input; the filter object is derived from it above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const ladderColumns: readonly DataTableColumn<Notification>[] = [
    {
      id: 'contract',
      header: 'Contract',
      cell: (notification) => (
        <Link
          to={`/contracts/record/${encodeURIComponent(notification.occurrence.contract_key)}`}
        >
          {notification.occurrence.contract_key}
        </Link>
      ),
    },
    { id: 'about', header: 'Alert', cell: (n) => n.contract_summary_line },
    { id: 'watch', header: 'Watching', cell: (n) => n.watch_type },
    {
      id: 'tier',
      header: 'Tier',
      cell: (n) => (
        <>
          <span className="tag bad">{n.occurrence.tier}</span>
          <br />
          <span className="mut">
            {n.days_ahead === null
              ? 'past due — re-fires daily'
              : `${String(n.days_ahead)} days ahead`}
          </span>
        </>
      ),
    },
    { id: 'date', header: 'Date warned about', cell: (n) => n.occurrence.occurrence_date },
    { id: 'sent', header: 'Sent', cell: (n) => n.sent_utc },
  ];

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      <div className="card">
        <label htmlFor="attention-tags">Tags (all of) — filters both panels</label>
        <TagInput id="attention-tags" mode="filter" value={tags} onChange={setTags} />
      </div>

      <div className="card">
        <h3>Alerted and not acknowledged</h3>
        <p>
          Read straight from the notification ladder, in the order the rules put it. The tier
          arithmetic is server-side, next to the idempotency key that makes each alert fire once
          per tier — this page ranks nothing itself.
        </p>

        {ladder.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading the ladder…
          </p>
        ) : null}

        {ladder.kind === 'problem' ? (
          <>
            <ErrorNotice
              kind={ladder.problem}
              detail={ladder.detail}
              correlation_id={ladder.correlation_id}
            />
            {ladder.problem === 'not_implemented' ? (
              <p className="mut">
                The endpoint is <code>GET /v1/notifications</code>. Until the notification service
                writes the ledger, this panel stays empty of claims: showing an empty ladder here
                would assert that nothing needs attention, which is exactly how O-1 fails silently
                while the dashboard stays green.
              </p>
            ) : null}
          </>
        ) : null}

        {ladder.kind === 'ready' ? (
          <>
            <div className="totals">
              <div className="total">
                <span>Unacknowledged</span>
                <b>{ladder.response.totals.unacknowledged}</b>
              </div>
              <div
                className="total"
                data-tone={ladder.response.totals.past_due > 0 ? 'bad' : 'plain'}
              >
                <span>Past due</span>
                <b>{ladder.response.totals.past_due}</b>
              </div>
            </div>
            <DataTable
              caption="Notifications sent and not yet acknowledged, in ladder order"
              columns={ladderColumns}
              rows={ladder.response.notifications}
              rowKey={(n) =>
                `${n.occurrence.rule_id}|${n.occurrence.contract_key}|${n.occurrence.occurrence_date}|${n.occurrence.tier}`
              }
              empty={
                <p>
                  <strong>No unacknowledged alerts in your scope.</strong> Acknowledging an alert
                  stops its escalation, so an empty panel here means every open occurrence has been
                  acknowledged by someone — not that no dates are approaching.
                </p>
              }
            />
          </>
        ) : null}
      </div>

      <div className="card">
        <h3>Awaiting verification</h3>
        <p>
          A separate count, deliberately not fused with the panel above. Unverified fields and
          approaching dates are different kinds of overdue, and a single ranked list would be this
          browser inventing precedence between them — with two independently resolved scope
          snapshots underneath it.
        </p>

        {queue.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading the verification queue…
          </p>
        ) : null}

        {queue.kind === 'problem' ? (
          <ErrorNotice
            kind={queue.problem}
            detail={queue.detail}
            correlation_id={queue.correlation_id}
          />
        ) : null}

        {queue.kind === 'ready' ? (
          <>
            <div className="totals">
              <div className="total">
                <span>Pending</span>
                <b>{queue.response.totals.pending}</b>
              </div>
              <div className="total">
                <span>Pending, high stakes</span>
                <b>{queue.response.totals.pending_high_stakes}</b>
              </div>
              <div
                className="total"
                data-tone={queue.response.totals.escalated > 0 ? 'bad' : 'plain'}
              >
                <span>Escalated</span>
                <b>{queue.response.totals.escalated}</b>
              </div>
              <div className="total">
                <span>Oldest pending, days</span>
                <b>
                  {queue.response.totals.oldest_pending_age_days === null
                    ? '—'
                    : queue.response.totals.oldest_pending_age_days}
                </b>
              </div>
            </div>
            <p>
              <Link to="/governance/verification">Open the verification queue</Link> to confirm,
              correct or reject a field.
            </p>
          </>
        ) : null}
      </div>

      {showTypeDecisions ? <TypeDecisionsPanel /> : null}
    </>
  );
}

/** CLM-121 (R-121-15) — the samples among the decisions waiting, and what correcting one does. */
function safetySampleLine(samples: number): string {
  return (
    `${String(samples)} of these ${samples === 1 ? 'is an auto-approval safety sample' : 'are auto-approval safety samples'}. ` +
    'Check them as usual; correcting one pauses auto-approval.'
  );
}

/** The four counts the panel shows, in the Type proposals page's own tab names and order. */
const TYPE_DECISION_TILES = ['disagrees', 'pending', 'ai_decided', 'amendments'] as const;

/**
 * CLM-118 (R-118-16, B30) — how many type decisions wait for an administrator.
 * One read of the Type proposals endpoint, whose totals check their own
 * arithmetic; the counts are the tabs' (`typeProposalTabCounts`), so this panel
 * and that page never disagree. A 403 renders nothing: the panel is a
 * convenience for administrators, and a role the SPA could not name is
 * answered by the API alone.
 */
function TypeDecisionsPanel() {
  const [panel, setPanel] = useState<Panel<ContractTypeProposalsResponse>>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetchContractTypeProposals(controller.signal);
        if (controller.signal.aborted) return;
        setPanel({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPanel(toProblem(error));
      }
    })();
    return () => {
      controller.abort();
    };
  }, []);

  if (panel.kind === 'problem' && panel.problem === 'forbidden') return null;

  return (
    <div className="card" data-testid="attention-type-decisions">
      <h3>Contract types (administrators)</h3>
      <p>
        Type decisions waiting for an administrator. These cover all contracts in your scope, not filtered by tags: the
        type list takes no tag filter.
      </p>

      {panel.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading the type decisions…
        </p>
      ) : null}

      {panel.kind === 'problem' ? (
        <ErrorNotice kind={panel.problem} detail={panel.detail} correlation_id={panel.correlation_id} />
      ) : null}

      {panel.kind === 'ready' ? (
        <>
          <div className="totals">
            {TYPE_DECISION_TILES.map((tile) => {
              const count = typeProposalTabCounts(panel.response)[tile];
              return (
                <div key={tile} className="total" data-tone={tile === 'disagrees' && count > 0 ? 'bad' : 'plain'}>
                  <span>{TYPE_PROPOSALS_TAB_LABEL[tile]}</span>
                  <b>{count}</b>
                </div>
              );
            })}
          </div>
          {/* CLM-121 (R-121-15) — the samples are visible here too, and say what a correction does. */}
          {panel.response.totals.auto_approval_samples > 0 ? (
            <p data-testid="attention-type-safety-samples">{safetySampleLine(panel.response.totals.auto_approval_samples)}</p>
          ) : null}
          <p>
            <Link to="/admin/type-proposals">Open Type proposals</Link> to confirm, correct or type a contract, or to
            dismiss a “possibly an amendment” flag.
          </p>
        </>
      ) : null}
    </div>
  );
}
